/**
 * Arena / corral panel renderer
 *
 * Plan view of a free-standing pipe panel or gate: the run of pipe between its
 * two pin connections. Gates add the swing of the gate leaf.
 *
 * Each item: { id, start, end, kind: 'panel' | 'gate', productId, lengthFt,
 *              heightIn, rails, label, gateOpeningFt }
 */

const SELECTION_COLOR = '#00c8ff';
const PANEL_COLOR = '#c8ccd2';
const GATE_COLOR = '#ffb347';

export function drawPanel(ctx, panel, isSelected = false, scale = 1) {
  const { start, end } = panel;
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length < 1) return;
  const ux = dx / length, uy = dy / length;
  const isGate = panel.kind === 'gate';
  const color = isSelected ? SELECTION_COLOR : isGate ? GATE_COLOR : PANEL_COLOR;

  ctx.save();
  ctx.lineCap = 'round';

  // Pipe run - drawn at least a few screen pixels wide so it reads at site scale
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.lineWidth = Math.max(5, 7 / scale);
  ctx.beginPath();
  ctx.moveTo(start.x, start.y);
  ctx.lineTo(end.x, end.y);
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, 4 / scale);
  ctx.stroke();

  if (isGate) {
    // Gate leaf swing, hinged at the start, over the gate opening
    const opening = Math.min(length, (panel.gateOpeningFt || panel.lengthFt || 12) * 40);
    const hingeOffset = (length - opening) / 2;
    const hx = start.x + ux * hingeOffset, hy = start.y + uy * hingeOffset;
    const angle = Math.atan2(uy, ux);
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, 1.5 / scale);
    ctx.setLineDash([6 / scale, 4 / scale]);
    ctx.beginPath();
    ctx.arc(hx, hy, opening, angle, angle + Math.PI / 2);
    ctx.lineTo(hx, hy);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Pin connections at both ends
  const pin = Math.max(4, 5 / scale);
  ctx.fillStyle = isSelected ? SELECTION_COLOR : '#5a6068';
  [start, end].forEach(p => {
    ctx.beginPath();
    ctx.arc(p.x, p.y, pin, 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.restore();
}

/**
 * Preview of the panel being placed with the Panel tool
 */
export function drawPanelPreview(ctx, preview, scale = 1) {
  if (!preview?.start || !preview?.end) return;
  ctx.save();
  ctx.globalAlpha = 0.6;
  drawPanel(ctx, preview, true, scale);
  ctx.restore();
}

const distanceToSegment = (p, a, b) => {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
};

export function hitTestPanel(panel, pos, scale = 1) {
  return distanceToSegment(pos, panel.start, panel.end) <= Math.max(8, 10 / scale);
}
